import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    FiArrowRight,
    FiCheckCircle,
    FiClock,
    FiEye,
    FiPlus,
    FiRefreshCw,

    FiShield,
    FiXCircle,
} from "react-icons/fi";
import { MdOutlineLiveTv } from "react-icons/md";
import { MyContext } from "../../Context/MyProvider";
import { showTost } from "../../showTost";
import ThemedPageFrame from "./ThemedPageFrame";
import {
    getXsrfTokenCookie,
    SHOP_API_TOKEN_STORAGE_KEY,
    SHOP_RECHARGE_ENDPOINTS,
    isUserLoggedIn,
    requireLogin,
} from "../../utils/auth";
import {
    openRechargeRazorpay,
    saveRechargePaymentStatus,
} from "../../utils/rechargePayment";
import "../../Style/style.css";

const AMOUNT_PRESETS = [100, 199, 299, 399, 499, 799, 999];

const getList = (data, key) => {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data?.[key])) return data.data[key];
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const firstValue = (object, keys = []) => {
    for (const key of keys) {
        const value = key
            .split(".")
            .reduce((current, part) => current?.[part], object);

        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }

    return "";
};

const getErrorMessage = (error) => {
    const response = error?.response?.data;
    const responseMessage = response?.msg || response?.message;

    if (responseMessage) {
        try {
            const parsed =
                typeof responseMessage === "string"
                    ? JSON.parse(responseMessage)
                    : responseMessage;

            return parsed?.msg || parsed?.message || responseMessage;
        } catch {
            return responseMessage;
        }
    }

    return error?.message || "Unable to complete the request.";
};

const getAuthConfig = () => {
    const token =
        getXsrfTokenCookie() ||
        localStorage.getItem(SHOP_API_TOKEN_STORAGE_KEY);

    return {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    };
};

const getInitials = (name = "") => {
    const clean = String(name).trim();

    if (!clean) return "TV";

    const words = clean.split(/\s+/);

    if (words.length >= 2) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }

    return clean.slice(0, 2).toUpperCase();
};

const maskCustomerId = (customerId = "") =>
    customerId.length > 5
        ? `${customerId.slice(0, 3)} **** ${customerId.slice(-4)}`
        : customerId;

const formatDate = (value) => {
    if (!value) return "Date unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getStatus = (value) => String(value || "pending").toLowerCase();

const getStatusMeta = (status) => {
    if (["success", "successful", "completed"].includes(status)) {
        return {
            label: "Successful",
            className: "success",
            icon: <FiCheckCircle />,
        };
    }

    if (["failed", "failure", "cancelled"].includes(status)) {
        return {
            label: "Failed",
            className: "failed",
            icon: <FiXCircle />,
        };
    }

    return {
        label: "Pending",
        className: "pending",
        icon: <FiClock />,
    };
};

const looksLikeDthHistory = (item = {}) => {
    const searchable = [
        item?.operator_type,
        item?.service_name,
        item?.operator_name,
        item?.operator,
        item?.recharge_type,
        item?.type,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (item?.customer_email || item?.customer_number || item?.customer_no || item?.p1) {
        return false;
    }

    if (/^[6-9]\d{9}$/.test(String(item?.mobile_no || "").replace(/\D/g, ""))) {
        return false;
    }

    if (
        searchable.includes("dth") ||
        searchable.includes("direct to home") ||
        searchable.includes("dish tv") ||
        searchable.includes("tata play") ||
        searchable.includes("sun tv") ||
        searchable.includes("sun direct") ||
        searchable.includes("videocon")
    ) {
        return true;
    }

    return Boolean(
        item?.customer_id ||
        item?.subscriber_id ||
        (
            item?.mobile_no &&
            !/^[6-9]\d{9}$/.test(String(item.mobile_no).replace(/\D/g, ""))
        )
    );
};

const isSuccessfulResponse = (data = {}) => {
    if (data?.status === false || data?.success === false) return false;
    if (String(data?.ResponseCode || data?.response_code || "") === "400") return false;

    return true;
};

const DthRecharge = () => {
    const { setIsLoginShow, isLogin, BaseUrl } = useContext(MyContext);
    const navigate = useNavigate();
    const location = useLocation();

    const serviceId = location.state?.serviceId || new URLSearchParams(location.search).get("serviceId");

    const [operators, setOperators] = useState([]);
    const [history, setHistory] = useState([]);
    const [plans, setPlans] = useState([]);

    const [customerId, setCustomerId] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [selectedOperator, setSelectedOperator] = useState(null);
    // const [manualOperatorCode, setManualOperatorCode] = useState("");
    const [amount, setAmount] = useState("");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // const [operatorSearch, setOperatorSearch] = useState("");
    const [showRechargeForm, setShowRechargeForm] = useState(false);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [paymentResponse, setPaymentResponse] = useState(null);

    const loadHistory = async () => {
        try {
            const response = await axios.get(
                SHOP_RECHARGE_ENDPOINTS.history,
                getAuthConfig()
            );

            setHistory(getList(response.data, "data").filter(looksLikeDthHistory));
        } catch {
            setHistory([]);
        }
    };

    useEffect(() => {
        if (!isUserLoggedIn()) {
            setLoading(false);
            return;
        }

        const loadDthData = async () => {
            try {
                setError("");

                const config = getAuthConfig();
                const [
                    operatorResponse,
                    historyResponse,
                    plansResponse,
                ] = await Promise.all([
                    axios.get(
                        `https://shop.nabhiverse.com/api/v1/operator/list/${serviceId}`,
                        config
                    ),
                    axios.get(SHOP_RECHARGE_ENDPOINTS.history, config),
                    axios.get(
                        "https://shop.nabhiverse.com/api/v1/plans",
                        config
                    ),
                ]);

                const availableOperators =
                    operatorResponse?.data?.response || [];

                const allPlans =
                    plansResponse?.data?.response ||
                    plansResponse?.data?.data ||
                    [];

                const availablePlans = allPlans.filter(
                    (plan) =>
                        String(plan?.service_id) === String(serviceId)
                );

                setOperators(availableOperators);
                setPlans(availablePlans);

                setHistory(
                    getList(historyResponse.data, "data").filter(
                        looksLikeDthHistory
                    )
                );
            } catch (requestError) {
                setError(getErrorMessage(requestError));
            } finally {
                setLoading(false);
            }
        };

        loadDthData();
    }, [isLogin, serviceId]);
    const getOperatorPlans = (operator) => {
        if (
            !operator?.operator_id ||
            !Array.isArray(operator?.operator_recharge_plan) ||
            operator.operator_recharge_plan.length === 0
        ) {
            return [];
        }

        return plans.filter(
            (plan) =>
                String(plan?.service_id) === String(serviceId) &&
                String(plan?.operator_id) ===
                String(operator.operator_id)
        );
    };
    const selectedOperatorPlans = useMemo(
        () => getOperatorPlans(selectedOperator),
        [selectedOperator, plans, serviceId]
    );

    const amountLimits = useMemo(
        () => ({
            min: Number(selectedOperator?.operator_min_recharge_amount || 10),
            max: Number(selectedOperator?.operator_max_recharge_amount || 100000),
        }),
        [selectedOperator]
    );

    const operatorCode = selectedOperator?.operator_code;
    const visibleHistory = showAllHistory ? history : history.slice(0, 5);

    const validateForm = () => {
        if (!customerId.trim() || customerId.trim().length < 4) {
            return "Enter a valid DTH customer ID or subscriber ID.";
        }

        if (!operatorCode) {
            return "Select a DTH operator or enter the operator code.";
        }

        if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
            return "Enter a valid 10-digit mobile number.";
        }

        if (
            !amount ||
            Number(amount) < amountLimits.min ||
            Number(amount) > amountLimits.max
        ) {
            return `Enter an amount between Rs.${amountLimits.min} and Rs.${amountLimits.max}.`;
        }

        return "";
    };

    const buildPayload = () => ({
        service_type: "DTH",
        customer_id: customerId.trim(),
        mobile_no: mobileNumber,
        operator_code: String(operatorCode).trim(),
        amount: String(amount),
        partner_request_id: `DTH${Date.now()}`,
        dest: "online",
        user_var1: "",
        user_var2: "",
        user_var3: "",
    });

    const handleRecharge = async () => {
        if (!requireLogin(setIsLoginShow)) return;

        const validationMessage = validateForm();

        if (validationMessage) {
            showTost(validationMessage, "error");
            return;
        }

        const payload = buildPayload();

        setSubmitting(true);
        setPaymentResponse(null);

        try {
            const payment = await openRechargeRazorpay({
                baseUrl: BaseUrl,
                amount: payload.amount,
                description: `DTH recharge for ${customerId.trim()}`,
                serviceLabel: "DTH Recharge",
            });

            const response = await axios.post(
                SHOP_RECHARGE_ENDPOINTS.order,
                {
                    ...payload,
                    razorpay_payment_id: payment?.razorpay_payment_id,
                    razorpay_order_id: payment?.razorpay_order_id,
                    razorpay_signature: payment?.razorpay_signature,
                    payment_status: "success",
                },
                getAuthConfig()
            );

            const data = response?.data || {};

            if (!isSuccessfulResponse(data)) {
                throw new Error(data?.message || data?.msg || "DTH recharge request failed.");
            }

            setPaymentResponse(data);
            saveRechargePaymentStatus({
                status: "success",
                serviceLabel: "DTH Recharge",
                amount: payload.amount,
                message: data?.message || data?.ResponseMsg || "DTH recharge request submitted successfully.",
                requestId: data?.request_id || payload.partner_request_id,
                razorpayOrderId: payment?.razorpay_order_id,
                razorpayPaymentId: payment?.razorpay_payment_id,
                retryPath: "/dth-recharge",
            });

            showTost(
                data?.message ||
                data?.ResponseMsg ||
                "DTH recharge request submitted successfully.",
                "success"
            );

            setAmount("");
            await loadHistory();
            navigate("/recharge-payment-status?status=success");
        } catch (requestError) {
            const message = getErrorMessage(requestError);

            saveRechargePaymentStatus({
                status: "failed",
                serviceLabel: "DTH Recharge",
                amount,
                message,
                retryPath: "/dth-recharge",
            });

            showTost(message, "error");
            await loadHistory();
            navigate("/recharge-payment-status?status=failed");
        } finally {
            setSubmitting(false);
        }
    };

    const openNewRecharge = () => {
        if (!requireLogin(setIsLoginShow)) return;

        setCustomerId("");
        setMobileNumber("");
        setSelectedOperator(null);
        setAmount("");
        // setOperatorSearch("");
        setPaymentResponse(null);
        setError("");
        setShowRechargeForm(true);

        window.setTimeout(() => {
            document
                .getElementById("dth-recharge-form")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const handleRechargeAgain = (item) => {
        if (!requireLogin(setIsLoginShow, "/dth-recharge")) return;

        const code = item?.operator_code || "";
        const operator = operators.find(
            (candidate) =>
                String(candidate.operator_code) === String(code)
        );
        setCustomerId(String(item?.customer_id || item?.subscriber_id || ""));
        setMobileNumber(String(item?.mobile_no || ""));
        setSelectedOperator(operator || null);
        // setManualOperatorCode(operator ? "" : String(code));
        setAmount(String(item?.amount || ""));
        // setOperatorSearch("");
        setPaymentResponse(null);
        setShowRechargeForm(true);

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleOperatorSelect = (operator) => {
        setSelectedOperator(operator);
        // setManualOperatorCode("");
        setPaymentResponse(null);
    };

    return (
        <ThemedPageFrame
            activePath="/dth-recharge"
            eyebrow="NabhiVerse Payments"
            title="DTH Recharge"
            subtitle="Recharge your TV connection quickly and securely."
            className="mobile_recharge_page dth_recharge_page"
        >
            <div className="mobile_recharge_shell">
                {error && (
                    <div className="mobile_recharge_alert">
                        <FiXCircle />
                        <span>{error}</span>
                        <button type="button" onClick={() => window.location.reload()}>
                            <FiRefreshCw />
                            Retry
                        </button>
                    </div>
                )}

                <section className="mobile_recharge_history_section">
                    <div className="mobile_recharge_section_header">
                        <div>
                            <span className="mobile_recharge_section_kicker">
                                Recharge activity
                            </span>
                            <h2>Recent DTH Recharges</h2>
                            <p>Recharge the same subscriber ID again in one tap.</p>
                        </div>

                        <div className="mobile_recharge_history_actions">
                            {history.length > 5 && (
                                <button
                                    type="button"
                                    className="mobile_recharge_text_button"
                                    onClick={() => setShowAllHistory((current) => !current)}
                                >
                                    <FiEye />
                                    {showAllHistory ? "Show less" : "View all"}
                                </button>
                            )}

                            <button
                                type="button"
                                className="mobile_recharge_new_button"
                                onClick={openNewRecharge}
                            >
                                <FiPlus />
                                New DTH Recharge
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mobile_recharge_history_grid">
                            {[1, 2, 3].map((item) => (
                                <div className="mobile_recharge_skeleton_card" key={item}>
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            ))}
                        </div>
                    ) : visibleHistory.length === 0 ? (
                        <div className="mobile_recharge_empty">
                            <div className="mobile_recharge_empty_icon">
                                <MdOutlineLiveTv />
                            </div>
                            <strong>No recent DTH recharges</strong>
                            <span>
                                Your DTH recharge activity will appear here after your first transaction.
                            </span>
                            <button type="button" onClick={openNewRecharge}>
                                <FiPlus />
                                Start a DTH Recharge
                            </button>
                        </div>
                    ) : (
                        <div className="mobile_recharge_history_grid">
                            {visibleHistory.map((item, index) => {
                                const statusMeta = getStatusMeta(
                                    getStatus(item?.status || item?.transaction?.status)
                                );

                                return (
                                    <article
                                        className="mobile_recharge_history_card"
                                        key={item?.id || index}
                                        onClick={() => handleRechargeAgain(item)}
                                    >
                                        <div className="mobile_recharge_history_card_top">
                                            <div className="mobile_recharge_operator_logo">
                                                {getInitials(item?.operator_name || "DTH")}
                                            </div>

                                            <div className="mobile_recharge_history_identity">
                                                <strong>{item?.operator_name || "DTH Recharge"}</strong>
                                                <span>
                                                    {maskCustomerId(
                                                        String(item?.customer_id || item?.subscriber_id || "")
                                                    )}
                                                </span>
                                            </div>

                                            <div className="mobile_recharge_history_price">
                                                <strong>Rs.{item?.amount || "0"}</strong>
                                                <span>{formatDate(item?.created_at)}</span>
                                            </div>
                                        </div>

                                        <div className="mobile_recharge_history_card_bottom">
                                            <span className="mobile_recharge_circle_tag">
                                                Code {item?.operator_code || "-"}
                                            </span>

                                            <span className={`mobile_recharge_status ${statusMeta.className}`}>
                                                {statusMeta.icon}
                                                {statusMeta.label}
                                            </span>

                                            <span className="mobile_recharge_recharge_again">
                                                Recharge again
                                                <FiArrowRight />
                                            </span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section
                    id="dth-recharge-form"
                    className={`mobile_recharge_form_section ${showRechargeForm ? "is-visible" : "is-hidden-mobile"
                        }`}
                >
                    <div className="mobile_recharge_form_header">
                        <div>
                            <span className="mobile_recharge_section_kicker">
                                DTH
                            </span>
                            <h2>Recharge DTH</h2>
                            <p>Enter subscriber details and confirm before payment.</p>
                        </div>

                        <div className="mobile_recharge_secure_badge">
                            <FiShield />
                            Secure payment
                        </div>
                    </div>

                    <div className="mobile_recharge_form_card">
                        <div className="mobile_recharge_field">
                            <label className="mobile_recharge_label" htmlFor="dth-customer-id">
                                Customer ID / Subscriber ID
                            </label>

                            <div className="mobile_recharge_phone_input dth_recharge_customer_input">
                                <span>ID</span>
                                <input
                                    id="dth-customer-id"
                                    value={customerId}
                                    onChange={(event) => {
                                        setCustomerId(event.target.value.replace(/[^a-zA-Z0-9-]/g, ""));
                                        setPaymentResponse(null);
                                    }}
                                    placeholder="Enter DTH customer ID"
                                />
                            </div>
                        </div>

                        {customerId.trim().length >= 4 && (
                            <div className="mobile_recharge_field">
                                <label className="mobile_recharge_label" htmlFor="dth-mobile-number">
                                    Mobile number
                                </label>

                                <div className="mobile_recharge_phone_input">
                                    <span>+91</span>
                                    <input
                                        id="dth-mobile-number"
                                        inputMode="numeric"
                                        maxLength="10"
                                        value={mobileNumber}
                                        onChange={(event) => {
                                            setMobileNumber(event.target.value.replace(/\D/g, ""));
                                            setPaymentResponse(null);
                                        }}
                                        placeholder="Enter 10-digit mobile number"
                                    />
                                </div>
                            </div>
                        )}

                        {customerId.trim().length >= 4 && (
                            <div className="mobile_recharge_step mobile_recharge_step_reveal">
                                <div className="mobile_recharge_step_content">
                                    <div className="mobile_recharge_field">
                                        <div className="mobile_recharge_label_row">
                                            <label className="mobile_recharge_label">
                                                Select DTH operator
                                            </label>
                                            <span className="mobile_recharge_selected_text">
                                                {selectedOperator?.operator_name || "Choose one"}
                                            </span>
                                        </div>

                                        <div className="mobile_recharge_operator_grid">
                                            {loading ? (
                                                [1, 2, 3, 4].map((item) => (
                                                    <div className="mobile_recharge_operator_skeleton" key={item} />
                                                ))
                                            ) : operators.length === 0 ? (
                                                <div className="mobile_recharge_no_operator">
                                                    No DTH operators found from API. Enter operator code below.
                                                </div>
                                            ) : (
                                                operators.map((operator) => {
                                                    const isSelected =
                                                        selectedOperator?.operator_code === operator.operator_code;

                                                    return (
                                                        <button
                                                            className={`mobile_recharge_operator_card ${isSelected ? "selected" : ""
                                                                }`}
                                                            key={operator.id || operator.operator_id}
                                                            type="button"
                                                            onClick={() => handleOperatorSelect(operator)}
                                                        >
                                                            <span className="mobile_recharge_operator_logo">
                                                                {operator.operator_image ? (
                                                                    <img
                                                                        src={operator.operator_image}
                                                                        alt={operator.operator_name}
                                                                        className="mobile_recharge_operator_image"
                                                                    />
                                                                ) : (
                                                                    getInitials(operator.operator_name)
                                                                )}
                                                            </span>

                                                            <span>{operator.operator_name}</span>
                                                            {/* <span>{operator.displayName}</span> */}
                                                            {isSelected && (
                                                                <FiCheckCircle className="mobile_recharge_operator_check" />
                                                            )}
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                

                        {operatorCode && (
                            <div className="mobile_recharge_step mobile_recharge_step_reveal">
                                <div className="mobile_recharge_step_content">
                                    <div className="mobile_recharge_field">
                                        <div className="mobile_recharge_label_row">
                                            <label className="mobile_recharge_label">
                                                Select amount
                                            </label>
                                            <span className="mobile_recharge_amount_limit">
                                                Rs.{amountLimits.min} - Rs.{amountLimits.max}
                                            </span>
                                        </div>

                                        {selectedOperatorPlans.length > 0 ? (
                                            <div className="mobile_recharge_plan_grid">
                                                {selectedOperatorPlans.map((plan, index) => (
                                                    <button
                                                        type="button"
                                                        key={`${plan.operator_id}-${plan.amount}-${plan.duration}-${index}`}
                                                        className={`mobile_recharge_plan_card ${Number(amount) === Number(plan.amount)
                                                            ? "selected"
                                                            : ""
                                                            }`}
                                                        onClick={() => {
                                                            setAmount(String(plan.amount));
                                                            setPaymentResponse(null);
                                                        }}
                                                    >
                                                        <div className="mobile_recharge_plan_top">
                                                            <strong>
                                                                ₹{plan.amount}
                                                            </strong>

                                                            <span>
                                                                {plan.duration} Days
                                                            </span>
                                                        </div>

                                                        {plan.description && (
                                                            <p>{plan.description}</p>
                                                        )}

                                                        {plan.channels &&
                                                            Object.keys(plan.channels).length > 0 && (
                                                                <div className="mobile_recharge_plan_channels">
                                                                    {Object.values(plan.channels)
                                                                        .slice(0, 4)
                                                                        .map((channel) => (
                                                                            <span key={channel}>
                                                                                {channel}
                                                                            </span>
                                                                        ))}
                                                                </div>
                                                            )}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        )}

                        {customerId && mobileNumber && operatorCode && amount && (
                            <div className="mobile_recharge_summary">
                                <div>
                                    <span>Recharge to</span>
                                    <strong>{customerId}</strong>
                                </div>

                                <div>
                                    <span>Mobile</span>
                                    <strong>+91 {mobileNumber}</strong>
                                </div>

                                <div>
                                    <span>Operator</span>
                                    <strong>
                                        {selectedOperator?.operator_name || `Code ${operatorCode}`}
                                    </strong>
                                </div>

                                <div>
                                    <span>Amount</span>
                                    <strong>Rs.{amount || "0"}</strong>
                                </div>
                            </div>
                        )}

                        {paymentResponse && (
                            <div className="pay_bills_success_box">
                                <FiCheckCircle />
                                <div>
                                    <strong>
                                        {paymentResponse?.message ||
                                            paymentResponse?.ResponseMsg ||
                                            "DTH recharge submitted."}
                                    </strong>
                                    {(paymentResponse?.request_id ||
                                        paymentResponse?.partner_request_id) && (
                                            <span>
                                                Request ID:{" "}
                                                {paymentResponse.request_id ||
                                                    paymentResponse.partner_request_id}
                                            </span>
                                        )}
                                    {paymentResponse?.transaction?.rechargekit_order_id && (
                                        <span>
                                            Order ID:{" "}
                                            {paymentResponse.transaction.rechargekit_order_id}
                                        </span>
                                    )}
                                    {paymentResponse?.transaction?.status && (
                                        <span>Status: {paymentResponse.transaction.status}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            className="mobile_recharge_submit"
                            type="button"
                            disabled={loading || submitting || !customerId || !mobileNumber || !operatorCode || !amount}
                            onClick={handleRecharge}
                        >
                            {submitting ? (
                                <>
                                    <span className="mobile_recharge_spinner" />
                                    Processing recharge...
                                </>
                            ) : (
                                <>
                                    Recharge DTH
                                    <FiArrowRight />
                                </>
                            )}
                        </button>

                        <p className="mobile_recharge_disclaimer">
                            By continuing, you confirm the DTH customer ID, operator and amount are correct.
                        </p>
                    </div>
                </section>
            </div>
        </ThemedPageFrame>
    );
};

export default DthRecharge;
